<script lang="ts" setup>
import { getCmsImage } from '@/composables/useCms';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  title: string;
  heading: string;
  body: string;
  size: string;
  overlay?: boolean;
  textSize?: string;
  images: {
    title: string;
    url: string;
  }[];
  callToAction: {
    label: string;
    title: string;
    text: string;
    customText: string;
    url: string;
  };
}

const props = defineProps<Props>();

// Use custom text if available, otherwise fall back to text
const ctaText = computed(() => {
  return props.callToAction.customText || props.callToAction.text;
});

const headingClass = computed(() => {
  const size = props.textSize?.toLowerCase();

  if (size == 'big') {
    return 'text-4xl lg:text-5xl';
  }

  return 'text-3xl lg:text-4xl';
});
</script>

<template>
  <div class="relative">
    <div :class="{
      'relative overflow-hidden border-0 w-full max-w-screen-2xl rounded-3xl mx-auto px-8': size == 'contained',
      'relative overflow-hidden border-0 w-full mx-auto': size != 'contained'
    }">
      <Card class="shadow-none!">
        <!-- Background Image -->
        <div class="absolute inset-0" v-if="images && images.length > 0">
          <NuxtImg
            :src="getCmsImage(images)"
            :alt="images[0]?.title || heading"
            :title="images[0]?.title || heading"
            format="webp"
            loading="lazy"
            class="w-full h-full object-cover"
          />
        </div>

        <!-- Dark overlay for better text readability -->
        <div v-if="overlay" class="absolute inset-0 bg-black/20"></div>

        <!-- Card Content -->
        <CardContent class="relative z-10 flex items-center justify-center py-12 lg:py-16">
          <div class="flex flex-col gap-6 text-center max-w-4xl mx-auto">
            <!-- Heading -->
            <h2 class="font-bold text-white mb-2" :class="headingClass">
              {{ heading ?? title }}
            </h2>

            <!-- Body Content -->
            <div v-if="body" v-html="body" class="prose prose-lg text-white text-center prose-headings:text-white mb-4 max-w-none" />

            <!-- Call to Action Button -->
            <div class="flex justify-center w-full items-center">
              <VButton
                v-if="callToAction"
                :href="(callToAction.url).replace('-cms','')"
                size="lg"
              >
                {{ callToAction.label }}
              </VButton>
            </div>
            <!-- End Call to Action Button -->
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
